import {getTestProps} from '@benchling/js-test-utils';
import {Box, createStyles, Group, Stack, Text} from '@mantine/core';
import React, {useEffect, useState} from 'react';
import {Panel, PanelGroup, PanelResizeHandle} from 'react-resizable-panels';

import SkillDetailPanel from 'llm/chat-library/skills/SkillDetailPanel';
import {SkillSummary} from 'llm/chat-library/skills/SkillsDAO';
import SkillsList from 'llm/chat-library/skills/SkillsList';
import UploadSkillModal from 'llm/chat-library/skills/UploadSkillModal';
import useSkills from 'llm/chat-library/skills/useSkills';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import EntityStore from 'stores/EntityStore';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import legacyRunModal from 'ui-library/legacyRunModal';
import {CenteredSpinner} from 'ui-library/Spinner';

const useStyles = createStyles((theme) => ({
  splitContainer: {
    display: 'flex',
    flex: '1 1 auto',
    minHeight: 0,
    overflow: 'hidden',
  },
  listStack: {
    flex: '1 1 auto',
    minHeight: 0,
    height: '100%',
    overflow: 'hidden',
    padding: '16px 24px',
  },
  listPanel: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
  },
  detailPanel: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
    borderLeft: `1px solid ${theme.colors.gray[3]}`,
  },
  panelGroup: {
    width: '100%',
    height: '100%',
  },
  resizer: {
    width: '6px',
    cursor: 'col-resize',
    backgroundColor: 'transparent',
    flexShrink: 0,
    ':hover': {
      backgroundColor: theme.colors.gray[2],
    },
  },
}));

export default function AISettingsSkills({isExpanded}: {isExpanded?: boolean}): JSX.Element {
  const {
    skills,
    isLoading: skillsLoading,
    archiveSkill,
    createNewSkills,
    refresh,
    enableSkill,
    disableSkill,
  } = useSkills();
  const isTenantAdmin = EntityStore.currentUser()?.isTenantAdmin ?? false;
  const [selectedSkillId, setSelectedSkillId] = useState<string | null>(null);
  const {classes} = useStyles();

  const selectedSkill = skills.find((s) => s.id === selectedSkillId) ?? null;

  useEffect(() => {
    if (!isExpanded) {
      setSelectedSkillId(null);
    }
  }, [isExpanded]);

  function handleSelectSkill(skill: SkillSummary): void {
    if (selectedSkillId === skill.id) {
      setSelectedSkillId(null);
      return;
    }
    setSelectedSkillId(skill.id);
    SidepanelActions.expand();
  }

  function handleCloseDetail(): void {
    setSelectedSkillId(null);
  }

  const listContent = (
    <Stack spacing="lg" className={classes.listStack}>
      <Stack spacing="sm">
        <Group noWrap={true} position="apart" align="center">
          <Text fw="bold" size="xl">
            Skills
          </Text>
          <Button
            buttonStyle={ButtonStyle.SECONDARY}
            icon="plus"
            onClick={() => {
              legacyRunModal(UploadSkillModal, {
                onRequestSubmit: createNewSkills,
              });
            }}
            size={ButtonSize.SMALL}
            text="Add"
            {...getTestProps({component: 'AISettingsSkills', element: 'uploadSkillButton'})}
          />
        </Group>
        <Text fw="normal" size="sm" c="dimmed">
          Reusable instructions to extend AI agent capabilities.
        </Text>
      </Stack>
      {skillsLoading ? (
        <CenteredSpinner />
      ) : (
        <SkillsList
          skills={skills}
          isTenantAdmin={isTenantAdmin}
          onArchive={archiveSkill}
          onEnable={enableSkill}
          onDisable={disableSkill}
          selectedSkillId={selectedSkill?.id}
          onSelectSkill={handleSelectSkill}
        />
      )}
    </Stack>
  );

  if (!selectedSkill) {
    return listContent;
  }

  return (
    <Box className={classes.splitContainer}>
      <PanelGroup direction="horizontal" className={classes.panelGroup}>
        <Panel defaultSize={40} minSize={25} maxSize={70}>
          <Box className={classes.listPanel}>{listContent}</Box>
        </Panel>
        <PanelResizeHandle className={classes.resizer} />
        <Panel defaultSize={60} minSize={30} maxSize={75}>
          <Box className={classes.detailPanel}>
            <SkillDetailPanel skill={selectedSkill} onClose={handleCloseDetail} onRefresh={refresh} />
          </Box>
        </Panel>
      </PanelGroup>
    </Box>
  );
}
